import { Component, ErrorInfo, ReactNode } from 'react';
import styled from 'styled-components';
import { Button } from 'reactstrap';
import { logException } from '@paradigm/logging/src/logging';
import { ErrorBoundaryError } from '@paradigm/logging/src/errors';

interface Props {
  readonly children: ReactNode;
  readonly fallback?: ReactNode;
}

interface State {
  readonly hasError: boolean;
}

// Session-scoped guard so a genuinely-broken build can't reload-loop: we
// auto-reload for a chunk error at most once per this interval. A reload
// is near-instant, so a build that re-throws the same chunk error hits
// this window and falls through to the manual fallback instead of looping.
// A later, unrelated deploy (> the interval) still auto-recovers.
const CHUNK_RELOAD_AT_KEY = 'pdgm.chunkReloadAt';
const CHUNK_RELOAD_MIN_INTERVAL_MS = 10_000;

// A code-split chunk failed to load — almost always a stale tab pointing at
// a [contenthash] bundle that a deploy replaced. webpack tags these
// `ChunkLoadError`; the message variants cover dynamic import + CSS chunks
// across browsers. A false positive only costs one guarded reload.
function isChunkLoadError(error: Error): boolean {
  return (
    error.name === 'ChunkLoadError' ||
    /Loading(?: CSS)? chunk [\w-]+ failed/iu.test(error.message) ||
    /Importing a module script failed/iu.test(error.message)
  );
}

function shouldAutoReloadForChunkError(): boolean {
  try {
    const last =
      Number(window.sessionStorage.getItem(CHUNK_RELOAD_AT_KEY)) || 0;
    const now = Date.now();
    if (now - last < CHUNK_RELOAD_MIN_INTERVAL_MS) return false;
    window.sessionStorage.setItem(CHUNK_RELOAD_AT_KEY, String(now));
    return true;
  } catch {
    // sessionStorage unavailable (private mode / quota) — without the guard
    // we can't bound a loop, so don't auto-reload.
    return false;
  }
}

export default class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false };

  static getDerivedStateFromError(_: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  // eslint-disable-next-line class-methods-use-this
  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // A stale tab that survived a deploy can throw when it lazy-loads a
    // chunk the new build replaced. Recover automatically by reloading once
    // onto the current build instead of stranding the user on the manual
    // error page; the time guard prevents a loop if the build is broken.
    if (isChunkLoadError(error) && shouldAutoReloadForChunkError()) {
      window.location.reload();
      return;
    }
    logException(
      errorInfo.componentStack,
      new ErrorBoundaryError(error.message),
    );
  }

  override render() {
    const { fallback, children } = this.props;

    if (this.state.hasError) {
      return fallback == null ? <ErrorPage /> : fallback;
    }

    return children;
  }
}

function ErrorPage() {
  const reloadApp = () => window.location.reload();
  return (
    <ErrorContainer>
      <Heading>Something went wrong…</Heading>
      <Button onClick={reloadApp}>Reload app</Button>
    </ErrorContainer>
  );
}

const ErrorContainer = styled.div`
  margin: 3rem;
  text-align: center;
`;

const Heading = styled.h2`
  opacity: 0.7;
  color: #ffffff;
  font-size: 18px;
  font-weight: 400;
  font-family: 'IBM Plex Sans', sans-serif;
  padding-bottom: 12px;
`;
